/**
 * Salesforce Enhanced Web Chat Bootstrapper
 * ----------------------------------------
 * This module handles the complete lifecycle of the Salesforce Messaging Enhanced Web Chat widget.
 *
 * INITIALIZATION FLOW:
 * 1. Environment Check: `isEnvironmentAllowed` verifies if the current URL is in the allowed/excluded list.
 * 2. Script Loading: `loadBootstrapScript` injects the Salesforce `bootstrap.min.js` into the document.
 * 3. Bootstrap Wait: `waitForBootstrapReady` polls until `window.embeddedservice_bootstrap` is available.
 * 4. Widget Init: `initEnhancedWebChat` calls `esb.init` with the orgId, channel, and site URLs.
 * 5. Ready Event: The widget fires `onEmbeddedMessagingReady` when it's fully loaded and APIs are available.
 * 6. Prechat Config: Once ready, `setHiddenPrechatFields` and `setVisiblePrechatFields` are called.
 * 7. Error Handling: A timeout (15s) monitors if `onReady` is ever called (detects CORS/network issues).
 *
 * EXPORTED FUNCTIONS:
 * - `bootEnhancedWebChat`: The main orchestrator (called by the React hook/provider).
 * - `launchChat`: Programmatically opens the chat window.
 * - `sendTextMessage`: Programmatically sends a message (e.g., for "Quick Ask" buttons).
 *
 * For more info check the official documentation:
 * @see https://developer.salesforce.com/docs/service/messaging-web/references/m4w-reference/overview.html
 */

import { ENHANCED_CHAT_CONFIG as CFG, EnhancedChatConfig } from './salesforce-chat-widget-config';
import safeWindow from '../../../../ui/utils/safeWindows';
import './types';
import { isUrlAllowed } from '../../../../ui/utils/urlUtils';
import { WIDGET_BUTTON_POSITION } from '../../../../ui/constants';

const LOG_PREFIX = '[EnhancedWebChat]';

const log = {
  info: (...args: unknown[]) => console.info(LOG_PREFIX, ...args),
  warn: (...args: unknown[]) => console.warn(LOG_PREFIX, ...args),
  error: (...args: unknown[]) => console.error(LOG_PREFIX, ...args),
};

let loadAttempts = 0;
let scriptElement: HTMLScriptElement | null = null;
let isInitialized = false;

/**
 * Checks if the current environment is allowed based on the configuration.
 * Uses the shared isUrlAllowed utility for consistency across the app.
 */
export function isEnvironmentAllowed(allowedUrls?: string[], excludedUrls?: string[]): boolean {
  const allowed = isUrlAllowed(allowedUrls || [], excludedUrls || []);

  if (!allowed) {
    log.warn('Environment not allowed for chat widget');
  } else {
    log.info('Environment allowed for chat widget');
  }

  return allowed;
}

/**
 * Waits for the Salesforce bootstrap object to be available on the window.
 */
export function waitForBootstrapReady(
  timeoutMs = CFG.bootstrapReadyTimeoutMs ?? 8000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const checkInterval = 150;

    const tick = () => {
      if (safeWindow.embeddedservice_bootstrap) {
        return resolve();
      }
      if (Date.now() - startTime > timeoutMs) {
        return reject(new Error('Timeout waiting for Salesforce bootstrap'));
      }
      setTimeout(tick, checkInterval);
    };
    tick();
  });
}

/**
 * Loads the Salesforce bootstrap script.
 */
export function loadBootstrapScript(src: string = CFG.bootstrapSrc): Promise<void> {
  return new Promise((resolve, reject) => {
    if (safeWindow.embeddedservice_bootstrap) {
      log.info('Bootstrap already present.');
      return resolve();
    }

    if (scriptElement) {
      log.info('Script already inserted; waiting for availability...');
      void waitForBootstrapReady().then(resolve).catch(reject);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      log.info('bootstrap.min.js loaded');
      void waitForBootstrapReady().then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error('Error loading Salesforce bootstrap script'));

    scriptElement = script;
    document.head.appendChild(script);
  });
}

/**
 * Initializes the Enhanced Web Chat.
 * Should be called after the bootstrap script is ready.
 */
export function initEnhancedWebChat({
  config,
  buttonPosition,
  restrictSessionOnMessagingChannel,
  shouldMinimizeWindowOnNewTab,
}: {
  config: EnhancedChatConfig;
  buttonPosition: string;
  restrictSessionOnMessagingChannel: boolean;
  shouldMinimizeWindowOnNewTab: boolean;
}): void {
  if (isInitialized) {
    log.info('initEnhancedWebChat() skipped — already initialized.');
    return;
  }

  const esb = safeWindow.embeddedservice_bootstrap;
  if (!esb) {
    log.warn('embeddedservice_bootstrap not available for initialization');
    return;
  }

  // Here we can set the language and other settings before widget initialization
  esb.settings = esb.settings || {};
  esb.settings.language = config.language ?? 'es';
  esb.settings.restrictSessionOnMessagingChannel = restrictSessionOnMessagingChannel;
  esb.settings.shouldMinimizeWindowOnNewTab = shouldMinimizeWindowOnNewTab;
  esb.settings.chatButtonPosition = buttonPosition;

  esb.init(config.orgId, config.channelName, config.siteBaseURL, { scrt2URL: config.scrt2URL });
  isInitialized = true;
  log.info(`Enhanced Web Chat initialized: Listening for onEmbeddedMessagingReady event...`);
}

/**
 * Sets hidden prechat fields.
 */
export function setHiddenPrechatFields(fields: Record<string, string>): void {
  const esb = safeWindow.embeddedservice_bootstrap;
  if (!esb?.prechatAPI?.setHiddenPrechatFields) {
    log.warn('prechatAPI.setHiddenPrechatFields not available');
    return;
  }
  esb.prechatAPI.setHiddenPrechatFields(fields);
}

/**
 * Sets visible prechat fields.
 */
export function setVisiblePrechatFields(
  fields: Record<string, { value: string; isEditableByEndUser?: boolean }>,
): void {
  const esb = safeWindow.embeddedservice_bootstrap;
  if (!esb?.prechatAPI?.setVisiblePrechatFields) {
    log.warn('prechatAPI.setVisiblePrechatFields not available');
    return;
  }
  esb.prechatAPI.setVisiblePrechatFields(fields);
}

/**
 * Launches the chat widget.
 */
export function launchChat(): void {
  safeWindow.embeddedservice_bootstrap?.utilAPI?.launchChat();
}

/**
 * La API oficial de Enhanced Web Chat dice:
 * embeddedservice_bootstrap.utilAPI.sendTextMessage(message)
 * Envía un mensaje como si lo hubiera escrito el usuario final, dentro de una sesión activa.
 * Actúa como un “autocompletar” del input del usuario para botones de “Preguntas rápidas”.
 * No envía mensajes si la conversación no está activa.
 */
export function sendTextMessage(message: string): void {
  log.info('Sending text message:', message);
  safeWindow.embeddedservice_bootstrap?.utilAPI?.sendTextMessage(message);
}

/**
 * Orchestrates the loading and initialization of the Enhanced Web Chat.
 */
export function bootEnhancedWebChat(
  hiddenPrechat?: Record<string, string>,
  visiblePrechat?: Record<string, { value: string; isEditableByEndUser?: boolean }>,
  allowedUrls?: string[],
  excludedUrls?: string[],
  buttonPosition: string = WIDGET_BUTTON_POSITION,
  restrictSessionOnMessagingChannel: boolean = false,
  shouldMinimizeWindowOnNewTab: boolean = true,
  bootDelaySeconds: number = 0,
): (() => void) | undefined {
  if (!isEnvironmentAllowed(allowedUrls, excludedUrls)) {
    const errorMsg = 'Boot cancelled — Environment not allowed.';
    log.info(errorMsg);
    safeWindow.dispatchEvent(
      new CustomEvent('onEmbeddedMessagingError', {
        detail: { message: errorMsg, type: 'ENVIRONMENT_NOT_ALLOWED' },
      }),
    );
    return;
  }

  let isReady = false;
  let bootTimeoutId: NodeJS.Timeout;
  let bootDelayTimeoutId: NodeJS.Timeout | undefined;
  const BOOT_TIMEOUT_MS = 15000; // 15 seconds to wait for the ready event

  const onReady = () => {
    isReady = true;
    if (bootTimeoutId) clearTimeout(bootTimeoutId);
    log.info('onEmbeddedMessagingReady event received.');
    if (hiddenPrechat) setHiddenPrechatFields(hiddenPrechat);
    if (visiblePrechat) setVisiblePrechatFields(visiblePrechat);
  };

  safeWindow.addEventListener('onEmbeddedMessagingReady', onReady);

  const attemptBoot = async () => {
    try {
      await loadBootstrapScript();
      initEnhancedWebChat({
        config: CFG,
        buttonPosition,
        restrictSessionOnMessagingChannel,
        shouldMinimizeWindowOnNewTab,
      });

      // Set a timeout to check if the ready event was ever received
      bootTimeoutId = setTimeout(() => {
        if (!isReady) {
          const errorMsg =
            'Initialization timeout: onEmbeddedMessagingReady not received. This might be due to CORS or network issues.';
          log.error(errorMsg);
          safeWindow.dispatchEvent(
            new CustomEvent('onEmbeddedMessagingError', {
              detail: { message: errorMsg, type: 'BOOT_TIMEOUT' },
            }),
          );
        }
      }, BOOT_TIMEOUT_MS);
    } catch (err) {
      loadAttempts += 1;
      log.error(`Boot attempt ${loadAttempts} failed`, err);

      const maxAttempts = CFG.maxLoadAttempts ?? 2;
      if (loadAttempts < maxAttempts) {
        const delay = CFG.retryDelayMs ?? 1500;
        log.info(`Retrying in ${delay}ms...`);
        setTimeout(() => {
          void attemptBoot();
        }, delay);
      } else {
        const errorMsg = 'Maximum boot attempts reached. Chat widget could not be initialized.';
        log.error(errorMsg);
        safeWindow.dispatchEvent(
          new CustomEvent('onEmbeddedMessagingError', {
            detail: { message: errorMsg, type: 'MAX_ATTEMPTS_REACHED' },
          }),
        );
      }
    }
  };

  if (bootDelaySeconds > 0) {
    log.info(`Boot delayed by ${bootDelaySeconds}s`);
    bootDelayTimeoutId = setTimeout(() => void attemptBoot(), bootDelaySeconds * 1000);
  } else {
    void attemptBoot();
  }

  return () => {
    safeWindow.removeEventListener('onEmbeddedMessagingReady', onReady);
    if (bootTimeoutId) clearTimeout(bootTimeoutId);
    if (bootDelayTimeoutId) clearTimeout(bootDelayTimeoutId);
  };
}
